import { useCallback, useEffect, useMemo, useState } from 'react';

import { trackEvent } from './gt';
import {
  currentSubscription,
  fetchPushConfig,
  offersInApp,
  permissionOf,
  pushSupportOf,
  readAppAsked,
  readSyncedAt,
  saveSubscription,
  subscriptionBody,
  syncDue,
  unsubscribeThisDevice,
  urlBase64ToUint8Array,
  workerRegistration,
  writeAppAsked,
  writeSyncedAt,
} from './push';
import { isStandalone, siteLocale } from './pwa';

const browserWindow = () => (typeof window === 'undefined' ? undefined : window);
const device = () => ({ userAgent: navigator.userAgent, maxTouchPoints: navigator.maxTouchPoints ?? 0, locale: siteLocale() });
const readToken = () => {
  try {
    return localStorage.getItem('authToken');
  } catch {
    return null;
  }
};

/**
 * Push notifications, for PushProvider (the signed-in pages), and only in the installed app (owner decision): in a
 * normal browser this asks the server nothing and offers nothing. The server's switch (GET /api/push/config), this
 * device's subscription (re-told to the server at most once a day), the opt-in sheet, and turning it off. The browser
 * asks only from the sheet's "Ativar" (a tap), never on page load.
 */
export const usePushState = ({ busy: shellBusy }) => {
  const [standalone] = useState(() => isStandalone());
  const [signedIn] = useState(() => Boolean(readToken()));
  const [enabled, setEnabled] = useState(false);
  const [publicKey, setPublicKey] = useState(null);
  const [permission, setPermission] = useState(() => permissionOf(browserWindow()));
  const [subscribed, setSubscribed] = useState(false);
  const [deviceChecked, setDeviceChecked] = useState(false);
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState('app');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const support = pushSupportOf(browserWindow());
  const inApp = standalone && signedIn && support === 'ok';

  // The server's switch
  useEffect(() => {
    if (!inApp) return undefined;
    let gone = false;
    fetchPushConfig(readToken())
      .then((config) => {
        if (gone) return;
        const key = typeof config?.publicKey === 'string' && config.publicKey ? config.publicKey : null;
        setEnabled(config?.enabled === true && key !== null);
        setPublicKey(key);
      })
      .catch(() => undefined);
    return () => {
      gone = true;
    };
  }, [inApp]);

  // This device's subscription; re-told to the server at most once a day, so its row stays in step
  useEffect(() => {
    if (!inApp || !enabled) return undefined;
    let gone = false;
    currentSubscription().then(async (subscription) => {
      if (gone) return;
      setSubscribed(subscription != null);
      setDeviceChecked(true);
      if (subscription && permissionOf(browserWindow()) === 'granted' && syncDue(readSyncedAt(), Date.now())) {
        await saveSubscription(subscriptionBody(subscription, device()), readToken()).catch(() => undefined);
        writeSyncedAt(Date.now());
      }
    });
    return () => {
      gone = true;
    };
  }, [inApp, enabled]);

  const openOptIn = useCallback((from) => {
    const now = permissionOf(browserWindow());
    setSource(from);
    setPermission(now);
    setDone(false);
    setError(now === 'denied' ? 'denied' : null);
    setOpen(true);
  }, []);

  const onEnable = useCallback(() => {
    const win = browserWindow();
    if (!publicKey || !win?.Notification?.requestPermission) {
      setError('failed');
      return;
    }
    setBusy(true);
    setError(null);
    // The browser's question, straight from the tap: Safari only asks inside the gesture
    const asked = win.Notification.requestPermission();
    (async () => {
      try {
        const answer = await asked;
        setPermission(answer);
        if (answer !== 'granted') {
          setError('denied');
          return;
        }
        const registration = await workerRegistration();
        if (!registration?.pushManager) throw new Error('no service worker');
        const subscription =
          (await registration.pushManager.getSubscription()) ??
          (await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) }));
        await saveSubscription(subscriptionBody(subscription, device()), readToken());
        writeSyncedAt(Date.now());
        setSubscribed(true);
        setDone(true);
        trackEvent('push_enabled', `Push - Enabled: ${source}`, 'pwa', true);
      } catch {
        setError('failed');
      } finally {
        setBusy(false);
      }
    })();
  }, [publicKey, source]);

  const disable = useCallback(async () => {
    await unsubscribeThisDevice(readToken());
    setSubscribed(false);
    trackEvent('push_disabled', 'Push - Disabled', 'pwa', true);
  }, []);

  // The installed app's first launch (or the first sign-in inside it): the sheet once, by itself
  useEffect(() => {
    if (open || !offersInApp({ standalone, signedIn, enabled, support, deviceChecked, subscribed, permission, busy: shellBusy, asked: readAppAsked() })) return;
    writeAppAsked(Date.now());
    openOptIn('app');
  }, [open, standalone, signedIn, enabled, support, deviceChecked, subscribed, permission, shellBusy, openOptIn]);

  const onClose = useCallback(() => {
    if (!busy) setOpen(false);
  }, [busy]);

  const available = inApp && enabled;
  const state = useMemo(
    () => ({ available, permission, subscribed, openOptIn, disable }),
    [available, permission, subscribed, openOptIn, disable]
  );
  const sheet = useMemo(() => ({ open, busy, done, error, onEnable, onClose }), [open, busy, done, error, onEnable, onClose]);

  return { state, sheet };
};
